import React from 'react'
import { Avatar, Button, Card, Col, Descriptions, Empty, Row, Space, Tag, Typography } from 'antd'
import {
  BankOutlined, EnvironmentOutlined, IdcardOutlined, MailOutlined,
  PhoneOutlined, ReloadOutlined, SafetyOutlined, UserOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import './ContactProfileDetails.css'

const { Paragraph, Text, Title } = Typography

const initials = name => String(name || '?').trim().slice(0, 2).toUpperCase()
const money = value => Number(value || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export default function ContactProfileDetails({
  contact,
  contactTypes = [],
  accountOptions = [],
  renderInsuranceStatus,
  onEdit,
  onRenewInsurance
}) {
  if (!contact) return <Empty description="ไม่พบข้อมูลผู้ติดต่อ" />

  const type = contactTypes.find(item => item.value === contact.type)
  const accountLabel = id => {
    if (!id) return 'ใช้ผังบัญชีเริ่มต้นของระบบ'
    return accountOptions.find(option => Number(option.value) === Number(id))?.label || `บัญชี #${id}`
  }
  const hasInsurance = contact.type !== 'supplier'
  const fullAddress = [contact.address, contact.postal_code].filter(Boolean).join(' ') || '-'

  return <div className="contact-profile">
    <Card bordered={false} className="contact-profile__hero">
      <div className="contact-profile__identity">
        <Avatar size={68} className="contact-profile__avatar">{initials(contact.name)}</Avatar>
        <div className="contact-profile__identity-text">
          <Space wrap size={8}>
            <Title level={3}>{contact.name}</Title>
            <Tag color={type?.color} icon={type?.icon}>{type?.label || contact.type || 'ไม่ระบุประเภท'}</Tag>
          </Space>
          <Text type="secondary">{contact.company_name || 'บุคคลทั่วไป / ไม่ได้ระบุชื่อกิจการ'}</Text>
          <Space wrap className="contact-profile__quick-contact">
            {contact.phone && <Button type="link" icon={<PhoneOutlined />} href={`tel:${contact.phone}`}>{contact.phone}</Button>}
            {contact.email && <Button type="link" icon={<MailOutlined />} href={`mailto:${contact.email}`}>{contact.email}</Button>}
          </Space>
        </div>
        {onEdit && <Button icon={<UserOutlined />} onClick={onEdit}>แก้ไขข้อมูล</Button>}
      </div>
    </Card>

    <Row gutter={[14, 14]}>
      <Col xs={24} lg={12}>
        <Card bordered={false} title={<Space><IdcardOutlined />ข้อมูลประจำตัวและกิจการ</Space>} className="contact-profile__section">
          <Descriptions column={1} colon={false} size="small">
            <Descriptions.Item label="ชื่อผู้ติดต่อหลัก">{contact.contact_person || '-'}</Descriptions.Item>
            <Descriptions.Item label="ชื่อบริษัท/กิจการ">{contact.company_name || '-'}</Descriptions.Item>
            <Descriptions.Item label="เลขประจำตัวผู้เสียภาษี">{contact.tax_id || '-'}</Descriptions.Item>
            <Descriptions.Item label="ประเภทความสัมพันธ์"><Tag color={type?.color}>{type?.label || '-'}</Tag></Descriptions.Item>
          </Descriptions>
        </Card>
      </Col>
      <Col xs={24} lg={12}>
        <Card bordered={false} title={<Space><EnvironmentOutlined />ช่องทางติดต่อ</Space>} className="contact-profile__section">
          <Descriptions column={1} colon={false} size="small">
            <Descriptions.Item label="โทรศัพท์">{contact.phone || '-'}</Descriptions.Item>
            <Descriptions.Item label="อีเมล">{contact.email || '-'}</Descriptions.Item>
            <Descriptions.Item label="ที่อยู่">{fullAddress}</Descriptions.Item>
          </Descriptions>
        </Card>
      </Col>
    </Row>

    <Card bordered={false} title={<Space><BankOutlined />ผังบัญชีที่ผูกกับรายชื่อนี้</Space>} className="contact-profile__section contact-profile__accounts">
      <Row gutter={[12, 12]}>
        <Col xs={24} md={12}><div><Text type="secondary">บัญชีลูกหนี้</Text><Text strong>{accountLabel(contact.receivable_account_id)}</Text></div></Col>
        <Col xs={24} md={12}><div><Text type="secondary">บัญชีเจ้าหนี้</Text><Text strong>{accountLabel(contact.payable_account_id)}</Text></div></Col>
        <Col xs={24} md={12}><div><Text type="secondary">บัญชีรายได้</Text><Text strong>{accountLabel(contact.revenue_account_id)}</Text></div></Col>
        <Col xs={24} md={12}><div><Text type="secondary">บัญชีค่าใช้จ่าย</Text><Text strong>{accountLabel(contact.expense_account_id)}</Text></div></Col>
      </Row>
    </Card>

    {hasInsurance && <Card
      bordered={false}
      title={<Space><SafetyOutlined />ข้อมูลประกันซอฟต์แวร์</Space>}
      extra={contact.insurance_end_date && onRenewInsurance ? <Button type="primary" icon={<ReloadOutlined />} onClick={onRenewInsurance}>ต่ออายุประกัน</Button> : null}
      className="contact-profile__section"
    >
      <Descriptions column={{ xs: 1, sm: 2 }} colon={false} size="small">
        <Descriptions.Item label="สถานะ">{renderInsuranceStatus ? renderInsuranceStatus(contact) : contact.insurance_status || '-'}</Descriptions.Item>
        <Descriptions.Item label="ค่าต่ออายุ">{contact.insurance_renewal_amount ? `${money(contact.insurance_renewal_amount)} บาท` : '-'}</Descriptions.Item>
        <Descriptions.Item label="วันเริ่มต้น">{contact.insurance_start_date ? dayjs(contact.insurance_start_date).format('DD/MM/YYYY') : '-'}</Descriptions.Item>
        <Descriptions.Item label="วันหมดอายุ">{contact.insurance_end_date ? dayjs(contact.insurance_end_date).format('DD/MM/YYYY') : '-'}</Descriptions.Item>
        <Descriptions.Item label="หมายเหตุประกัน" span={2}>{contact.insurance_notes || '-'}</Descriptions.Item>
      </Descriptions>
    </Card>}

    <Card bordered={false} title="หมายเหตุเกี่ยวกับรายชื่อนี้" className="contact-profile__section">
      <Paragraph className="contact-profile__notes">{contact.notes || 'ไม่มีหมายเหตุเพิ่มเติม'}</Paragraph>
    </Card>
  </div>
}
